import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';

const PaymentMethodModal = ({ isOpen, onClose, paymentMethod, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    type: 'bank_transfer',
    description: '',
    instructions: '',
    active: true,
    order: 0
  });

  const paymentTypes = [
    { value: 'barion', label: 'Barion' },
    { value: 'simplepay', label: 'SimplePay (OTP)' },
    { value: 'bank_transfer', label: 'Banki átutalás' },
    { value: 'cash', label: 'Készpénz / Utánvét' },
    { value: 'paypal', label: 'PayPal' },
    { value: 'stripe', label: 'Stripe' },
    { value: 'other', label: 'Egyéb' }
  ];

  useEffect(() => {
    if (paymentMethod) {
      setFormData({
        name: paymentMethod.name || '',
        type: paymentMethod.type || 'bank_transfer',
        description: paymentMethod.description || '',
        instructions: paymentMethod.instructions || '',
        active: paymentMethod.active !== undefined ? paymentMethod.active : true,
        order: paymentMethod.order || 0
      });
    } else {
      setFormData({
        name: '',
        type: 'bank_transfer',
        description: '',
        instructions: '',
        active: true,
        order: 0
      });
    }
  }, [paymentMethod, isOpen]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm overflow-y-auto">
      <div className="glass rounded-2xl max-w-2xl w-full my-8">
        <div className="p-6 border-b border-white/10 flex justify-between items-center">
          <h2 className="text-2xl font-semibold">
            {paymentMethod ? 'Fizetési mód szerkesztése' : 'Új fizetési mód'}
          </h2>
          <button onClick={onClose} className="text-2xl hover:text-yellow-200 transition">
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Name */}
          <div>
            <label className="block text-sm text-gray-400 mb-2">Név *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              className="w-full px-4 py-3 bg-black/30 border border-white/10 rounded-lg focus:border-yellow-200/50 focus:outline-none text-white"
              placeholder="pl. Barion, Banki átutalás"
            />
          </div>

          {/* Type */}
          <div>
            <label className="block text-sm text-gray-400 mb-2">Típus *</label>
            <select
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value })}
              required
              className="w-full px-4 py-3 bg-black/30 border border-white/10 rounded-lg focus:border-yellow-200/50 focus:outline-none text-white"
            >
              {paymentTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm text-gray-400 mb-2">Rövid leírás</label>
            <input
              type="text"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-4 py-3 bg-black/30 border border-white/10 rounded-lg focus:border-yellow-200/50 focus:outline-none text-white"
              placeholder="pl. Bankkártyás fizetés"
            />
          </div>

          {/* Instructions */}
          <div>
            <label className="block text-sm text-gray-400 mb-2">Utasítások / Információk</label>
            <textarea
              value={formData.instructions}
              onChange={(e) => setFormData({ ...formData, instructions: e.target.value })}
              rows="5"
              className="w-full px-4 py-3 bg-black/30 border border-white/10 rounded-lg focus:border-yellow-200/50 focus:outline-none text-white resize-none"
              placeholder="pl. Bankszámlaszám utaláshoz, további információk..."
            />
            <p className="text-xs text-gray-500 mt-1">
              Ez jelenik meg a vásárlónak a pénztárban (ha van)
            </p>
          </div>

          {/* Order */}
          <div>
            <label className="block text-sm text-gray-400 mb-2">Sorrend</label>
            <input
              type="number"
              value={formData.order}
              onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
              className="w-full px-4 py-3 bg-black/30 border border-white/10 rounded-lg focus:border-yellow-200/50 focus:outline-none text-white"
              placeholder="0"
            />
            <p className="text-xs text-gray-500 mt-1">
              Alacsonyabb szám = előrébb jelenik meg
            </p>
          </div>

          {/* Active Toggle */}
          <div className="flex items-center gap-3 p-4 bg-white/5 rounded-lg">
            <input
              type="checkbox"
              id="active"
              checked={formData.active}
              onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
              className="w-5 h-5 rounded border-white/20 bg-black/30 text-yellow-200 focus:ring-yellow-200/50"
            />
            <label htmlFor="active" className="text-white cursor-pointer">
              Aktív (látható a vásárlóknak)
            </label>
          </div>

          {/* Submit Buttons */}
          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 bg-white/5 hover:bg-white/10 rounded-lg transition"
            >
              Mégse
            </button>
            <button
              type="submit"
              className="btn-magic rounded-lg px-6 py-3"
            >
              {paymentMethod ? 'Módosítás' : 'Létrehozás'}
            </button>
          </div>
        </form>
      </div>

      <style jsx>{`
        .glass {
          background: linear-gradient(180deg, hsl(0 0% 100% / 0.08), hsl(0 0% 100% / 0.04));
          border: 1px solid hsl(0 0% 100% / 0.1);
          backdrop-filter: blur(12px);
        }
      `}</style>
    </div>
  );
};

export default PaymentMethodModal;
